import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import {
  buildMeasureResult,
  type CoordinateMeasureMode,
  computeAreaBoundaryIndices,
  type MeasureDraftState,
  type MeasurePoint,
  type MeasurePointRemovalEvent,
  type MeasurePointRemovalReason,
  type MeasureResult,
  type MeasureType,
  type MeasureUnitPreferenceResolver,
} from '@/plugins/tools/measure/MeasureData'
import { createIdGenerator } from '@/plugins/tools/shared/idGenerator'
import {
  AsyncHoverPickCoalescer,
  AsyncPickQueue,
  prefersAsyncToolPicking,
} from '@/plugins/tools/shared/toolPickPolicy'
import {
  getActiveViewCamera,
  getOptionalViewRegistry,
  isActiveViewFrame,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import type {
  Disposable,
  InputConsumer,
  NormalizedKeyEvent,
  NormalizedPointerEvent,
} from '@/shared/types/core'
import type { ActivatableToolPlugin, ActivationToken } from '@/shared/types/plugin'
import type {
  ClipService,
  InputRouter,
  PickingService,
  ViewEventRouter,
  ViewInputConsumer,
  ViewKeyEvent,
  ViewPointerEvent,
  ViewportCamera,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext, ViewId, ViewRect } from '@/shared/types/view'
import {
  isDisplayUnit,
  isFeetFormat,
  normalizeUnitPreference,
  type UnitPreference,
} from '@/shared/unitPresentation'
import { ndcToRay } from '@/shared/utils/geometry'
import { Magnifier } from '@/shared/utils/Magnifier'
import { screenToViewNDC } from '@/shared/utils/viewRect'
import {
  type MeasureCaptureConfig,
  type MeasureSnapResult,
  MeasureSnapService,
  type ResolveMeasureSnapOptions,
} from './MeasureSnapService'

type MeasurePointerEvent = NormalizedPointerEvent | ViewPointerEvent
type MeasureKeyEvent = NormalizedKeyEvent | ViewKeyEvent
const generateMeasurePointId = createIdGenerator('measure-point')

/**
 * hover 异步拾取的最小间隔。
 *
 * WebGPU 的 GPU 拾取本身是一次额外的 ID pass（会与当前帧抢 GPU），因此 hover 预览按上限频率
 * 更新即可：数值过大预览明显滞后，过小则每帧都在抢 GPU。
 */
const HOVER_PICK_MIN_INTERVAL_MS = 80

/** 测量工具插件配置。 */
export interface MeasurePluginOptions {
  /** 工具互斥组，默认与其他工具同属 default 组。 */
  group?: string
  /** 面积后续点到前三点平面的最大垂直距离，单位为米；默认 0.02（20mm）。 */
  areaPlaneTolerance?: number
}

/**
 * 测量工具插件，支持距离/面积/角度/高度/圆/方位角/坐标等测量。
 */
export class MeasurePlugin implements ActivatableToolPlugin {
  /** 插件名称 */
  readonly name = 'measure'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 50
  /** 所属互斥组 */
  readonly group: string
  /** 依赖的服务名列表 */
  readonly dependencies = ['PickingService', 'InputRouter'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['MeasureService'] as const

  /** 测量结果可见性状态 */
  private readonly resultVisibility = new Map<string, boolean>()
  private readonly draftChangeHandlers = new Set<(state: MeasureDraftState) => void>()
  private readonly pointRemovalHandlers = new Set<(event: MeasurePointRemovalEvent) => void>()
  private _active = false
  private _measureType: MeasureType = 'distance'
  private _coordinateMode: CoordinateMeasureMode = 'single'
  private areaPlaneConstraintEnabled = true
  private coordinatePreviewLabel: string | null = null
  private hasCoordinatePreviewLabel = false
  private activationToken: ActivationToken | null = null
  private cursorBeforeActivate: string | null = null
  private context!: EngineContext
  private inputRouter!: InputRouter
  private viewEventRouter: ViewEventRouter | null = null
  private usingViewEventRouter = false
  private pickingService!: PickingService
  private clipService: ClipService | null = null
  private snapService!: MeasureSnapService
  private results: MeasureResult[] = []
  private unitPreferenceResolver: MeasureUnitPreferenceResolver | null = null
  private readonly resultUnitPreferenceResolvers = new WeakMap<
    MeasureResult,
    MeasureUnitPreferenceResolver
  >()
  private currentPoints: MeasurePoint[] = []
  private draftViewId: ViewId | null = null
  private previewViewId: ViewId | null = null
  private previewPoint: MeasurePoint | null = null
  /** hover 异步拾取：单在途 + 最小间隔 + 过期结果丢弃。 */
  private readonly hoverPick = new AsyncHoverPickCoalescer(HOVER_PICK_MIN_INTERVAL_MS)
  /**
   * 最近一次命中锚点。
   *
   * WebGPU 每次 GPU 拾取都要跑一遍整份 splat 的顶点处理，不可能逐帧拾取；因此两次拾取之间用
   * 「光标射线 × 锚点切平面」做近似预览点，保证 overlay 跟手，拾取回读后再校正到精确点。
   */
  private hoverAnchor: { point: THREE.Vector3; viewId: string | null; at: number } | null = null
  /** 点击异步拾取：串行保序 + 令牌丢弃迟到结果。 */
  private readonly clickPickQueue = new AsyncPickQueue()
  private readonly hoverAnchorPlane = new THREE.Plane()
  private readonly hoverAnchorNormal = new THREE.Vector3()
  private readonly hoverAnchorHit = new THREE.Vector3()
  private hoverSnap: {
    kind: MeasureSnapResult['kind']
    snapType: MeasureSnapResult['snapType']
    entityId?: string
    assetId?: string
  } | null = null
  private currentCamera: THREE.Camera | null = null
  private magnifier: Magnifier | null = null
  private magnifierShowTimer: ReturnType<typeof globalThis.setTimeout> | null = null
  private viewRegistry: ViewRegistry | null = null
  private escapeArmed = false
  private readonly lastCameraMatrixWorld = new THREE.Matrix4()
  private readonly lastCameraProjectionMatrix = new THREE.Matrix4()
  private hasCameraSnapshot = false
  private lastCameraMotionAt = -Infinity
  private pendingPointer: {
    button: number
    startScreen: THREE.Vector2
    viewId: ViewId | null
    moved: boolean
  } | null = null

  private static readonly CLICK_MOVE_THRESHOLD = 5
  /** hover 锚点有效期：超过后不再用它做近似预览，避免长时间未拾取时预览停在错误位置。 */
  private static readonly HOVER_ANCHOR_TTL_MS = 600
  private static readonly GSPLAT_HOVER_RADIUS = 0.08
  private static readonly CAMERA_MOTION_COOLDOWN_MS = 120
  private static readonly MAGNIFIER_SHOW_DELAY_MS = 120
  /** 建筑点云面积测量的默认平面容差，单位为米（0.02m = 20mm）。 */
  private static readonly DEFAULT_AREA_PLANE_TOLERANCE = 0.02
  private static readonly AREA_POINT_DISTANCE_EPSILON = 1e-9
  private static readonly AREA_DIRECTION_CROSS_EPSILON = 1e-8
  private readonly areaPlaneTolerance: number
  private readonly handleViewActivated = (_event: {
    viewId: string
    previousViewId: string | null
  }) => {
    this.currentCamera = this.resolveActiveViewCamera()
  }

  private readonly consumer: InputConsumer & ViewInputConsumer = {
    name: 'measure',
    priority: 100,

    onPointerDown: (event: MeasurePointerEvent): boolean => {
      return this.handlePointerDown(event)
    },

    onPointerMove: (event: MeasurePointerEvent): boolean => {
      return this.handlePointerMove(event)
    },

    onPointerUp: (event: MeasurePointerEvent): boolean => {
      return this.handlePointerUp(event)
    },

    onWinKeyDown: (event: MeasureKeyEvent): boolean => {
      return this.handleKeyDown(event)
    },

    onWinKeyUp: (event: MeasureKeyEvent): boolean => {
      return this.handleKeyUp(event)
    },

    onDoubleClick: (): boolean => {
      this.finishCurrentMeasure()
      return true
    },
  }

  constructor(options: MeasurePluginOptions = {}) {
    const areaPlaneTolerance =
      options.areaPlaneTolerance ?? MeasurePlugin.DEFAULT_AREA_PLANE_TOLERANCE
    if (!Number.isFinite(areaPlaneTolerance) || areaPlaneTolerance < 0) {
      throw new RangeError('areaPlaneTolerance must be a finite non-negative number')
    }

    this.group = options.group ?? 'default'
    this.areaPlaneTolerance = areaPlaneTolerance
  }

  /** 是否处于激活状态 */
  get isActive(): boolean {
    return this._active
  }

  /**
   * 切换测量类型，若类型变化则清空当前采集点。
   *
   * @param type - 目标测量类型
   */
  setMeasureType(type: MeasureType): void {
    if (this._measureType === type) {
      this.emitResultChanged()
      return
    }

    this.resetDraftState()
    this._measureType = type
    this.emitDraftChanged()
    this.emitResultChanged()
  }

  /**
   * 获取当前测量类型。
   *
   * @returns 当前测量类型
   */
  getMeasureType(): MeasureType {
    return this._measureType
  }

  /**
   * 获取坐标测量采集模式。
   *
   * @returns 当前坐标测量采集模式
   */
  getCoordinateMode(): CoordinateMeasureMode {
    return this._coordinateMode
  }

  /** 设置当前测量会话的局部单位偏好解析器。 */
  setUnitPreferenceResolver(resolver?: MeasureUnitPreferenceResolver): void {
    this.unitPreferenceResolver = resolver ?? null
  }

  /** 设置当前测量会话是否启用面积平面约束。 */
  setAreaPlaneConstraintEnabled(enabled: boolean): void {
    this.areaPlaneConstraintEnabled = enabled
  }

  /** 获取当前测量会话是否启用面积平面约束。 */
  isAreaPlaneConstraintEnabled(): boolean {
    return this.areaPlaneConstraintEnabled
  }

  /** 解析草稿或完成结果实际使用的单位偏好。 */
  resolveUnitPreference(
    globalPreference: Readonly<UnitPreference>,
    result?: MeasureResult
  ): UnitPreference {
    const resolver = result
      ? this.resultUnitPreferenceResolvers.get(result)
      : this.unitPreferenceResolver
    if (!resolver) {
      return normalizeUnitPreference(globalPreference)
    }

    try {
      const override = resolver(globalPreference)
      if (!override || typeof override !== 'object') {
        return normalizeUnitPreference(globalPreference)
      }
      if (
        (override.displayUnit !== undefined && !isDisplayUnit(override.displayUnit)) ||
        (override.feetFormat !== undefined && !isFeetFormat(override.feetFormat))
      ) {
        return normalizeUnitPreference(globalPreference)
      }
      return normalizeUnitPreference({
        ...globalPreference,
        ...override,
      })
    } catch {
      return normalizeUnitPreference(globalPreference)
    }
  }

  /**
   * 设置坐标测量采集模式，模式变化时清空当前草稿。
   *
   * @param mode - 坐标测量采集模式
   */
  setCoordinateMode(mode: CoordinateMeasureMode): void {
    if (this._coordinateMode === mode) {
      return
    }

    this.resetDraftState()
    this._coordinateMode = mode
    this.emitDraftChanged()
    this.invalidateVisual('measure.coordinateMode')
  }

  /**
   * 获取当前测量草稿的业务快照。
   *
   * @returns 草稿状态
   */
  getDraftState(): MeasureDraftState {
    return {
      measureType: this._measureType,
      coordinateMode: this._coordinateMode,
      viewId: this.draftViewId,
      points: this.currentPoints.map((point) => ({
        ...point,
        position: point.position.clone(),
      })),
      canSave: this.canCompleteCurrentMeasure(),
    }
  }

  /**
   * 订阅已确认草稿状态变化。
   *
   * @param callback - 草稿变化回调
   * @returns 取消订阅句柄
   */
  onDraftChanged(callback: (state: MeasureDraftState) => void): Disposable {
    this.draftChangeHandlers.add(callback)
    return {
      dispose: () => {
        this.draftChangeHandlers.delete(callback)
      },
    }
  }

  /** 订阅测量点移除事件。 */
  onPointRemoved(callback: (event: MeasurePointRemovalEvent) => void): Disposable {
    this.pointRemovalHandlers.add(callback)
    return {
      dispose: () => {
        this.pointRemovalHandlers.delete(callback)
      },
    }
  }

  /**
   * 获取当前测量进行中已采集的点。
   *
   * @returns 已采集点的脱钩副本（点对象与坐标均已克隆），未进行测量时为空数组
   */
  getCurrentPoints(): ReadonlyArray<MeasurePoint> {
    return this.currentPoints.map((point) => ({
      ...point,
      position: point.position.clone(),
    }))
  }

  /**
   * 获取当前预览点（鼠标 hover 命中点）。
   *
   * @returns 预览点；未命中或未测量返回 null
   */
  getPreviewPoint(): MeasurePoint | null {
    return this.previewPoint
  }

  /**
   * 获取首点前预览所在视口
   * 坐标测量落点前用于约束预览标记只在当前鼠标所在视口显示
   */
  getPreviewViewId(): ViewId | null {
    return this.previewViewId
  }

  /**
   * 获取当前草稿所在视口
   * 测量落点后用于约束草稿和预览只在发起测量的视口显示
   */
  getDraftViewId(): ViewId | null {
    return this.draftViewId
  }

  getDraftResult(): MeasureResult | null {
    if (!this.canCompleteCurrentMeasure()) {
      return null
    }

    let areaBoundaryIndices: number[] | undefined
    if (this._measureType === 'area') {
      const computedBoundaryIndices = computeAreaBoundaryIndices(
        this.currentPoints,
        this.areaPlaneConstraintEnabled
      )
      if (!computedBoundaryIndices) {
        return null
      }
      areaBoundaryIndices = computedBoundaryIndices
    }

    return buildMeasureResult(this.currentPoints, this._measureType, {
      viewId: this.draftViewId,
      areaBoundaryIndices,
      areaPlaneConstraintEnabled: this.areaPlaneConstraintEnabled,
    })
  }

  canSaveDraft(): boolean {
    return this.getDraftResult() !== null
  }

  saveDraft(): MeasureResult | null {
    const result = this.getDraftResult()
    if (!result) {
      return null
    }

    if (this.unitPreferenceResolver) {
      this.resultUnitPreferenceResolvers.set(result, this.unitPreferenceResolver)
    }
    this.results.push(result)
    this.resetDraftState()
    this.emitDraftChanged()
    this.emitResultChanged()
    return result
  }

  undoLastPoint(): boolean {
    if (this.currentPoints.length === 0) {
      return false
    }

    return this.removeDraftPointAt(this.currentPoints.length - 1, 'undo')
  }

  /** 按稳定 ID 移除当前草稿中的指定点。 */
  removeDraftPoint(pointId: string): boolean {
    const pointIndex = this.currentPoints.findIndex((point) => point.id === pointId)
    if (pointIndex < 0) {
      return false
    }

    return this.removeDraftPointAt(pointIndex, 'remove')
  }

  private removeDraftPointAt(index: number, reason: MeasurePointRemovalReason): boolean {
    const [removedPoint] = this.currentPoints.splice(index, 1)
    if (!removedPoint) {
      return false
    }

    const lastPoint = this.currentPoints[this.currentPoints.length - 1] ?? null
    this.previewPoint = lastPoint ? { position: lastPoint.position.clone() } : null
    this.previewViewId = lastPoint ? this.draftViewId : null
    if (!lastPoint) {
      this.draftViewId = null
    }
    this.hoverSnap = null
    this.resetTransientState()
    if (removedPoint.id) {
      this.emitPointRemoved({
        reason,
        scope: 'draft',
        pointId: removedPoint.id,
        remainingPointCount: this.currentPoints.length,
      })
    }
    this.emitDraftChanged()
    this.emitResultChanged()
    return true
  }

  clearDraft(): boolean {
    if (this.currentPoints.length === 0 && this.previewPoint === null) {
      return false
    }

    this.currentPoints = []
    this.draftViewId = null
    this.previewPoint = null
    this.previewViewId = null
    this.hoverSnap = null
    this.pendingPointer = null
    this.emitDraftChanged()
    this.emitResultChanged()
    return true
  }

  /**
   * 更新草稿点的宿主显示标签。
   *
   * @param pointId - 点稳定标识
   * @param label - 显示标签；null 清除宿主标签
   * @returns 是否成功更新
   */
  setDraftPointLabel(pointId: string, label: string | null): boolean {
    const pointIndex = this.currentPoints.findIndex((point) => point.id === pointId)
    if (pointIndex < 0) {
      return false
    }

    const point = this.currentPoints[pointIndex]
    const normalizedLabel = label?.trim() || undefined
    if (point.label === normalizedLabel) {
      return true
    }

    this.currentPoints[pointIndex] = {
      ...point,
      label: normalizedLabel,
    }
    this.emitDraftChanged()
    this.invalidateVisual('measure.draftLabel')
    return true
  }

  /**
   * 设置连续坐标测量未确认预览点的宿主标签。
   *
   * @param label - 预览点标签；null 表示宿主接管但暂不显示，undefined 表示释放宿主标签所有权
   */
  setCoordinatePreviewLabel(label: string | null | undefined): void {
    if (label === undefined) {
      if (this.releaseCoordinatePreviewLabelOwnership()) {
        this.invalidateVisual('measure.coordinatePreviewLabel')
      }
      return
    }

    const normalizedLabel = label?.trim() || null
    if (this.hasCoordinatePreviewLabel && this.coordinatePreviewLabel === normalizedLabel) {
      return
    }

    this.hasCoordinatePreviewLabel = true
    this.coordinatePreviewLabel = normalizedLabel
    this.invalidateVisual('measure.coordinatePreviewLabel')
  }

  private releaseCoordinatePreviewLabelOwnership(): boolean {
    if (!this.hasCoordinatePreviewLabel) {
      return false
    }

    this.hasCoordinatePreviewLabel = false
    this.coordinatePreviewLabel = null
    return true
  }

  /**
   * 获取连续坐标测量未确认预览点的宿主标签。
   *
   * @returns 预览点标签
   */
  getCoordinatePreviewLabel(): string | null | undefined {
    return this.hasCoordinatePreviewLabel ? this.coordinatePreviewLabel : undefined
  }

  getHoverSnap() {
    return this.hoverSnap
  }

  setCaptureEnabled(enabled: boolean): void {
    this.snapService.setCaptureEnabled(enabled)
    this.emitResultChanged()
  }

  isCaptureEnabled(): boolean {
    return this.snapService.isCaptureEnabled()
  }

  setPointCloudCaptureEnabled(enabled: boolean): void {
    this.snapService.setPointCloudCaptureEnabled(enabled)
    this.emitResultChanged()
  }

  isPointCloudCaptureEnabled(): boolean {
    return this.snapService.isPointCloudCaptureEnabled()
  }

  setCadCaptureEnabled(enabled: boolean): void {
    this.snapService.setCadCaptureEnabled(enabled)
    this.emitResultChanged()
  }

  isCadCaptureEnabled(): boolean {
    return this.snapService.isCadCaptureEnabled()
  }

  setCaptureConfig(config: MeasureCaptureConfig): void {
    this.snapService.setCaptureConfig(config)
    this.emitResultChanged()
  }

  setOrthogonalEnabled(enabled: boolean): void {
    this.snapService.setOrthogonalEnabled(enabled)
    this.emitResultChanged()
  }

  isOrthogonalEnabled(): boolean {
    return this.snapService.isOrthogonalEnabled()
  }

  /**
   * 记录外部分配的激活令牌。
   *
   * @param token - 激活令牌
   */
  setActivationToken(token: ActivationToken): void {
    this.activationToken = token
  }

  /**
   * 初始化插件，注册 MeasureService。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    context.registerService('MeasureService', this)
  }

  /**
   * 设置指定测量结果的可见性
   */
  setResultVisible(id: string, visible: boolean): void {
    if ((this.resultVisibility.get(id) ?? true) === visible) {
      return
    }

    this.resultVisibility.set(id, visible)
    this.emitResultChanged()
  }

  /**
   * 获取指定测量结果的可见性
   */
  getResultVisible(id: string): boolean {
    return this.resultVisibility.get(id) ?? true
  }

  /** 启动插件，解析依赖并订阅视口事件 */
  onStart(): void {
    this.inputRouter = this.context.getService('InputRouter')
    this.viewEventRouter = this.context.hasService('ViewEventRouter')
      ? this.context.getService('ViewEventRouter')
      : null
    this.pickingService = this.context.getService('PickingService')
    this.clipService = this.context.hasService('ClipService')
      ? this.context.getService('ClipService')
      : null
    this.snapService = new MeasureSnapService(this.context, this.pickingService)
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.currentCamera = this.resolveActiveViewCamera()
    if (this.viewRegistry) {
      this.context.events.on('view.activated', this.handleViewActivated)
    }
  }

  /**
   * 每帧同步活动视口相机。
   *
   * @param frame - 视图帧上下文
   */
  onRenderView(frame: ViewFrameContext): void {
    if (!isActiveViewFrame(this.viewRegistry, frame)) return
    this.currentCamera = this.resolveActiveViewCamera() ?? frame.camera
    this.currentCamera.updateMatrixWorld(true)
    this.trackCameraMotion(this.currentCamera)
  }

  /** 销毁插件，取消订阅并停用测量 */
  onDestroy(): void {
    if (this.viewRegistry) {
      this.context.events.off('view.activated', this.handleViewActivated)
    }
    if (this._active && this.activationToken) {
      this.deactivate(this.activationToken)
    }
    this.disposeMagnifier()
    this.viewRegistry = null
    this.viewEventRouter = null
    this.clipService = null
    this.draftChangeHandlers.clear()
    this.pointRemovalHandlers.clear()
  }

  /**
   * 激活工具，注册输入消费者。
   *
   * @param token - 激活令牌
   */
  /** 光标宿主：走 GraphicsBackend 的 surface canvas，不读 legacy renderer。 */
  private cursorHost(): HTMLElement {
    return this.context.graphics.canvas
  }

  activate(token: ActivationToken): void {
    this.validateToken(token)
    if (this._active) return

    this._active = true
    this.cursorBeforeActivate = this.cursorHost().style.cursor
    this.cursorHost().style.cursor = 'crosshair'
    if (this.viewEventRouter) {
      this.viewEventRouter.addConsumer(this.consumer)
      this.usingViewEventRouter = true
      return
    }

    this.inputRouter.addConsumer(this.consumer)
    this.usingViewEventRouter = false
  }

  /**
   * 停用工具，移除输入消费者并结束进行中的测量。
   *
   * @param token - 激活令牌
   */
  deactivate(token: ActivationToken): void {
    this.validateToken(token)
    if (!this._active) return

    this._active = false
    if (this.usingViewEventRouter) {
      this.viewEventRouter?.removeConsumer(this.consumer)
    } else {
      this.inputRouter.removeConsumer(this.consumer)
    }
    this.usingViewEventRouter = false
    this.finishCurrentMeasure()
    this.hideMagnifier()
    this.cursorHost().style.cursor = this.cursorBeforeActivate ?? ''
    this.cursorBeforeActivate = null
    this.currentCamera = null
    this.resetTransientState()
    this.areaPlaneConstraintEnabled = true
    this.unitPreferenceResolver = null
    this.releaseCoordinatePreviewLabelOwnership()
    this.snapService.setCaptureEnabled(false)
    this.snapService.setPointCloudCaptureEnabled(false)
    this.snapService.setOrthogonalEnabled(false)
  }

  /**
   * 获取所有已保存的测量结果。
   *
   * @returns 测量结果数组，按插入顺序
   */
  getResults(): ReadonlyArray<MeasureResult> {
    return this.results
  }

  /**
   * 从外部注入已有的测量结果。
   *
   * @param result - 测量结果，会追加到列表末尾
   */
  addResult(result: MeasureResult): void {
    this.results.push(result)
    this.emitResultChanged()
  }

  /**
   * 按 ID 移除测量结果。
   *
   * @param id - 测量结果 ID；不存在时忽略
   */
  removeResult(id: string): void {
    const removedResult = this.results.find((result) => result.id === id)
    const prevLength = this.results.length
    this.results = this.results.filter((result) => result.id !== id)

    if (this.results.length !== prevLength) {
      if (removedResult) {
        this.resultUnitPreferenceResolvers.delete(removedResult)
      }
      this.resultVisibility.delete(id)
      this.emitResultChanged()
    }
  }

  /** 按稳定点 ID 移除已完成坐标结果中的指定点。 */
  removeResultPoint(resultId: string, pointId: string): boolean {
    const resultIndex = this.results.findIndex((result) => result.id === resultId)
    const result = this.results[resultIndex]
    if (!result || result.measureType !== 'coordinate') {
      return false
    }

    const pointIndex = result.points.findIndex((point) => point.id === pointId)
    if (pointIndex < 0) {
      return false
    }

    const remainingPoints = result.points.filter((_, index) => index !== pointIndex)
    const resolver = this.resultUnitPreferenceResolvers.get(result)
    this.resultUnitPreferenceResolvers.delete(result)

    if (remainingPoints.length === 0) {
      this.results.splice(resultIndex, 1)
      this.resultVisibility.delete(resultId)
    } else {
      const updatedResult = buildMeasureResult(remainingPoints, 'coordinate', {
        id: result.id,
        viewId: result.viewId,
      })
      this.results[resultIndex] = updatedResult
      if (resolver) {
        this.resultUnitPreferenceResolvers.set(updatedResult, resolver)
      }
    }

    this.emitPointRemoved({
      reason: 'remove',
      scope: 'result',
      pointId,
      resultId,
      remainingPointCount: remainingPoints.length,
    })
    this.emitResultChanged()
    return true
  }

  /** 清空所有测量结果 */
  clearResults(): void {
    if (this.results.length === 0) return

    for (const result of this.results) {
      this.resultUnitPreferenceResolvers.delete(result)
    }
    this.results = []
    this.resultVisibility.clear()
    this.emitResultChanged()
  }

  private handleKeyDown(event: NormalizedKeyEvent): boolean {
    if (!this._active) {
      return false
    }

    if (this.isControlKey(event.key)) {
      this.scheduleMagnifierShow()
      return false
    }

    if (event.ctrlKey && event.key.toLowerCase() === 'z') {
      this.hideMagnifier()
      this.undoLastPoint()
      event.domEvent.preventDefault()
      return true
    }

    this.cancelPendingMagnifierShow()

    if (event.key === 'Escape') {
      if (this.escapeArmed) {
        this.escapeArmed = false
        this.cancelCurrentMeasure()
        this.requestDeactivate()
        return true
      }

      this.clearDraft()
      this.escapeArmed = true
      return true
    }

    this.escapeArmed = false
    return false
  }

  private handleKeyUp(event: NormalizedKeyEvent): boolean {
    if (!this._active) {
      return false
    }

    if (!this.isControlKey(event.key)) {
      return false
    }

    this.hideMagnifier()
    return false
  }

  private requestDeactivate(): void {
    this.context.events.emit('tool.requestDeactivate', {
      name: this.name,
      group: this.group ?? 'default',
    })
  }

  private handlePointerDown(event: MeasurePointerEvent): boolean {
    if (event.button !== 0 && event.button !== 2) {
      return false
    }

    const eventViewId = this.resolveEventViewId(event)
    if (this.shouldIgnorePointerForDraft(eventViewId)) {
      return false
    }

    this.pendingPointer = {
      button: event.button,
      startScreen: event.screen.clone(),
      viewId: eventViewId,
      moved: false,
    }

    return false
  }

  // ========================================
  // author:Felix
  // task:位置测量坐标预览
  // explain：补齐坐标测量落点前随鼠标移动显示坐标的预览能力
  // ========================================

  /**
   * 处理测量过程中的鼠标移动
   * 测量激活后根据当前测量阶段更新悬停点，用于驱动坐标预览和后续测量辅助线
   */
  private handlePointerMove(event: MeasurePointerEvent): boolean {
    if (this.releaseClipBoxDrag()) {
      return false
    }

    const eventViewId = this.resolveEventViewId(event)
    if (this.shouldIgnorePointerForDraft(eventViewId)) {
      return false
    }

    if (this.pendingPointer) {
      const distance = event.screen.distanceTo(this.pendingPointer.startScreen)
      if (distance > MeasurePlugin.CLICK_MOVE_THRESHOLD) {
        this.pendingPointer.moved = true
      }
    }

    if (!this.resolveEventCamera(event)) {
      return false
    }

    if (this.shouldSuspendHoverPicking(event)) {
      this.hoverAnchor = null
      this.updateHoverState(null, null, null)
      return false
    }

    if (this.shouldUseAsyncHoverPick()) {
      if (this.clickPickQueue.pending > 0) {
        return false
      }
      this.updateApproximateHoverPreview(event)
      this.requestAsyncHoverPick(event)
      return false
    }

    const snapResult = this.pickPoint(event, 'move')
    const nextPreviewPoint = snapResult ? { position: snapResult.point.clone() } : null
    const nextHoverSnap = snapResult?.isCaptureHit
      ? {
          kind: snapResult.kind,
          snapType: snapResult.snapType,
          entityId: snapResult.entityId,
          assetId: snapResult.assetId,
        }
      : null

    this.updateHoverState(nextPreviewPoint, nextHoverSnap, eventViewId)

    return false
  }

  private handlePointerUp(event: MeasurePointerEvent): boolean {
    if (event.domEvent.type === 'pointercancel') {
      this.pendingPointer = null
      return false
    }

    if (this.releaseClipBoxDrag()) {
      return false
    }

    const pendingPointer = this.pendingPointer
    this.pendingPointer = null
    const eventViewId = this.resolveEventViewId(event)

    if (
      !pendingPointer ||
      pendingPointer.button !== event.button ||
      pendingPointer.moved ||
      (pendingPointer.viewId && eventViewId && pendingPointer.viewId !== eventViewId)
    ) {
      return false
    }

    if (this.shouldIgnorePointerForDraft(eventViewId)) {
      return false
    }

    if (event.button === 2) {
      if (event.domEvent.ctrlKey) {
        return this.undoLastPoint()
      }

      if (this.canCompleteCurrentMeasure()) {
        this.finishCurrentMeasure()
      } else {
        this.cancelCurrentMeasure()
      }

      return true
    }

    if (event.button !== 0 || !this.resolveEventCamera(event)) {
      return false
    }

    // WebGPU 没有同步 GPU 拾取，同步入口会回退到 CPU 遍历整份 splat（单次约 200ms）；
    // 点击改为异步后不再卡主线程，落点会在拾取读回后补上。
    if (this.shouldUseAsyncHoverPick()) {
      this.requestAsyncClickPick(event, eventViewId)
      return true
    }

    const snapResult = this.pickPoint(event, 'click')
    if (!snapResult) {
      if (this._measureType === 'area' && this.currentPoints.length >= 2) {
        this.updateHoverState(null, null, null)
      }
      return false
    }

    this.applyClickPickResult(snapResult, eventViewId)
    return true
  }

  /**
   * 应用一次点击拾取结果（同步与异步共用）。
   *
   * @param snapResult - 拾取结果
   * @param eventViewId - 事件所属 View
   * @returns 是否已经由自动完成结束当前测量
   */
  private applyClickPickResult(snapResult: MeasureSnapResult, eventViewId: string | null): boolean {
    if (!this.draftViewId) {
      this.draftViewId = eventViewId
    }
    this.currentPoints.push({
      id: generateMeasurePointId(),
      position: snapResult.point.clone(),
      tilesSource: snapResult.tilesSource,
    })
    this.hoverSnap = snapResult.isCaptureHit
      ? {
          kind: snapResult.kind,
          snapType: snapResult.snapType,
          entityId: snapResult.entityId,
          assetId: snapResult.assetId,
        }
      : null
    this.escapeArmed = false

    if (this.tryAutoFinish()) {
      return true
    }

    this.previewPoint = { position: snapResult.point.clone() }
    this.previewViewId = eventViewId
    this.emitDraftChanged()
    this.emitResultChanged()
    return false
  }

  /**
   * 发起一次异步点击拾取。
   *
   * 点击必须严格保序（连续点击对应连续落点），因此用 promise 链串行化；同时用 token 让
   * 「测量已结束 / 工具已停用」之后的迟到结果直接丢弃。点击在途期间暂停 hover 拾取，
   * 避免 hover 结果把刚点下的落点覆盖掉。
   */
  private requestAsyncClickPick(event: MeasurePointerEvent, eventViewId: string | null): void {
    const options = this.buildSnapOptions(event, 'click')
    if (!options) {
      return
    }

    // 作废在途 hover 结果，并让 click 期间不再发起新的 hover 拾取
    this.hoverPick.invalidate()
    this.clickPickQueue.enqueue(async (token) => {
      const snapResult = await this.snapService.resolvePointAsync(options)
      if (!this.clickPickQueue.isCurrent(token) || !this._active) {
        return
      }
      if (!snapResult) {
        if (this._measureType === 'area' && this.currentPoints.length >= 2) {
          this.updateHoverState(null, null, null)
        }
        return
      }

      this.hoverAnchor = {
        point: snapResult.point.clone(),
        viewId: eventViewId,
        at: performance.now(),
      }
      this.applyClickPickResult(snapResult, eventViewId)
    })
  }

  /**
   * 两次 GPU 拾取之间用近似点让 overlay 跟手。
   *
   * 以「光标射线与锚点切平面求交」得到近似世界点：拾取回读通常要几帧，若预览点只在回读时更新，
   * 用户会明显感到 overlay 拖在鼠标后面。近似点在同一个深度切平面上移动，画面上表现为平滑跟随；
   * 下一次拾取回读后会立即校正回精确表面点。
   */
  private updateApproximateHoverPreview(event: MeasurePointerEvent): void {
    const anchor = this.hoverAnchor
    if (!anchor) {
      return
    }
    if (performance.now() - anchor.at > MeasurePlugin.HOVER_ANCHOR_TTL_MS) {
      this.hoverAnchor = null
      return
    }

    const eventViewId = this.resolveEventViewId(event)
    if (anchor.viewId !== null && eventViewId !== null && anchor.viewId !== eventViewId) {
      this.hoverAnchor = null
      return
    }

    const camera = this.resolveEventCamera(event)
    if (!camera) {
      return
    }

    const viewRect = this.resolveEventViewRect(event, eventViewId)
    if (!viewRect || viewRect.width <= 0 || viewRect.height <= 0) {
      return
    }

    const ndc = screenToViewNDC(event.screen, viewRect)
    const ray = ndcToRay(ndc, camera)
    camera.getWorldDirection(this.hoverAnchorNormal)
    if (this.hoverAnchorNormal.lengthSq() === 0) {
      return
    }

    this.hoverAnchorPlane.setFromNormalAndCoplanarPoint(this.hoverAnchorNormal, anchor.point)
    const hit = ray.intersectPlane(this.hoverAnchorPlane, this.hoverAnchorHit)
    if (!hit) {
      return
    }

    this.updateHoverState({ position: hit.clone() }, this.hoverSnap, eventViewId)
  }

  /**
   * 拾取测量点
   * 根据当前交互类型和测量状态调用捕捉服务，返回点击落点或鼠标悬停预览点
   */
  private pickPoint(
    event: MeasurePointerEvent,
    interactionType: 'click' | 'move'
  ): MeasureSnapResult | null {
    const options = this.buildSnapOptions(event, interactionType)
    if (!options) {
      return null
    }

    return this.applyAreaPlaneConstraint(this.snapService.resolvePoint(options))
  }

  /** 构造捕捉服务入参；相机不可用时返回 null（与旧实现一致）。 */
  private buildSnapOptions(
    event: MeasurePointerEvent,
    interactionType: 'click' | 'move'
  ): ResolveMeasureSnapOptions | null {
    const eventViewId = this.resolveEventViewId(event)
    const eventViewRect = this.resolveEventViewRect(event, eventViewId)
    const eventCamera = this.resolveEventCamera(event)
    if (!eventCamera) {
      return null
    }

    return {
      screen: event.screen,
      camera: eventCamera,
      interactionType,
      centerSphereRadius:
        interactionType === 'move' ? MeasurePlugin.GSPLAT_HOVER_RADIUS : undefined,
      startPoint: this.currentPoints[this.currentPoints.length - 1]?.position ?? null,
      viewId: eventViewId,
      viewRect: eventViewRect,
      allowMoveWithoutStartPoint: interactionType === 'move',
    }
  }

  /**
   * WebGPU 的同步拾取没有 GPU 快路径，会回退到 CPU 遍历整份 splat（实测单次上百毫秒），
   * 因此 hover 预览必须走异步 GPU 拾取；WebGL2 保持原有同步拾取。
   */
  private shouldUseAsyncHoverPick(): boolean {
    return prefersAsyncToolPicking(this.context?.graphics)
  }

  /**
   * 发起一次异步 hover 拾取。
   *
   * 同一时刻只保留一个在途请求：GPU 读回需要若干帧，重复排队只会互相抢资源；期间新的鼠标位置
   * 会在下一次移动时带上最新坐标重新发起。结果回来时用 token 校验，避免过期结果覆盖新状态。
   */
  private requestAsyncHoverPick(event: MeasurePointerEvent): void {
    const options = this.buildSnapOptions(event, 'move')
    if (!options) {
      return
    }

    const eventViewId = options.viewId ?? null
    this.hoverPick.schedule(async (token) => {
      const snapResult = await this.snapService.resolvePointAsync(options)
      if (!this.hoverPick.isCurrent(token) || !this._active) {
        return
      }

      const constrained = this.applyAreaPlaneConstraint(snapResult)
      const nextPreviewPoint = constrained ? { position: constrained.point.clone() } : null
      const nextHoverSnap = constrained?.isCaptureHit
        ? {
            kind: constrained.kind,
            snapType: constrained.snapType,
            entityId: constrained.entityId,
            assetId: constrained.assetId,
          }
        : null

      this.hoverAnchor = constrained
        ? { point: constrained.point.clone(), viewId: eventViewId, at: performance.now() }
        : null
      this.updateHoverState(nextPreviewPoint, nextHoverSnap, eventViewId)
    })
  }

  /**
   * 对面积测量候选点应用定面、容差过滤和投影规则。
   *
   * 前两个点原样保留；第三点必须与前两点形成稳定平面；后续点只有位于平面
   * 配置的正负垂直容差内才有效，并统一返回在该平面上的正交投影。
   */
  private applyAreaPlaneConstraint(snapResult: MeasureSnapResult | null): MeasureSnapResult | null {
    if (!snapResult || this._measureType !== 'area' || !this.areaPlaneConstraintEnabled) {
      return snapResult
    }

    if (this.currentPoints.length < 2) {
      return snapResult
    }

    const first = this.currentPoints[0].position
    const second = this.currentPoints[1].position

    if (this.currentPoints.length === 2) {
      const firstDirection = new THREE.Vector3().subVectors(second, first)
      const candidateDirection = new THREE.Vector3().subVectors(snapResult.point, first)
      if (
        firstDirection.length() < MeasurePlugin.AREA_POINT_DISTANCE_EPSILON ||
        candidateDirection.length() < MeasurePlugin.AREA_POINT_DISTANCE_EPSILON
      ) {
        return null
      }

      const normalizedCrossLength = firstDirection
        .normalize()
        .cross(candidateDirection.normalize())
        .length()
      return normalizedCrossLength > MeasurePlugin.AREA_DIRECTION_CROSS_EPSILON ? snapResult : null
    }

    const third = this.currentPoints[2].position
    const plane = new THREE.Plane().setFromCoplanarPoints(first, second, third)
    if (Math.abs(plane.distanceToPoint(snapResult.point)) > this.areaPlaneTolerance) {
      return null
    }

    return {
      ...snapResult,
      point: plane.projectPoint(snapResult.point, new THREE.Vector3()),
    }
  }

  private shouldIgnorePointerForDraft(eventViewId: ViewId | null): boolean {
    return !!this.draftViewId && !!eventViewId && this.draftViewId !== eventViewId
  }

  private resolveEventViewId(event: MeasurePointerEvent | MeasureKeyEvent): ViewId | null {
    const routedViewId = (event as { viewId?: unknown }).viewId
    if (typeof routedViewId === 'string') {
      return routedViewId
    }
    return this.viewRegistry?.getActive()?.id ?? null
  }

  private resolveEventViewRect(
    event: MeasurePointerEvent,
    eventViewId: ViewId | null
  ): ViewRect | null {
    const routedViewRect = (event as { viewRect?: ViewRect }).viewRect
    if (routedViewRect) {
      return routedViewRect
    }
    if (event.viewport) {
      return event.viewport
    }
    if (eventViewId && this.viewRegistry) {
      return this.viewRegistry.get(eventViewId)?.rect ?? null
    }
    return this.resolveActiveViewRect()
  }

  private resolveEventCamera(event: MeasurePointerEvent): THREE.Camera | null {
    const eventViewId = this.resolveEventViewId(event)
    if (eventViewId && this.viewRegistry) {
      const viewportCamera = this.viewRegistry.getExtension(eventViewId, 'viewportCamera') as
        | ViewportCamera
        | undefined
      const camera = viewportCamera?.getActiveCamera()
      if (camera) {
        return camera
      }
    }
    return this.currentCamera ?? this.resolveActiveViewCamera()
  }

  private finishCurrentMeasure(): void {
    if (this.saveDraft()) {
      return
    }

    if (this._measureType === 'area' && this.currentPoints.length >= 3) {
      return
    }

    this.resetDraftState()
    this.emitDraftChanged()
    this.emitResultChanged()
  }

  private cancelCurrentMeasure(): void {
    if (this.currentPoints.length === 0 && this.previewPoint === null) {
      return
    }

    this.resetDraftState()
    this.emitDraftChanged()
    this.emitResultChanged()
  }

  private resetDraftState(): void {
    // 草稿被清空（完成/取消/停用）后，迟到的异步点击结果不得再落点
    this.clickPickQueue.invalidate()
    this.hoverPick.invalidate()
    this.hoverAnchor = null
    this.currentPoints = []
    this.draftViewId = null
    this.previewPoint = null
    this.previewViewId = null
    this.pendingPointer = null
    this.hoverSnap = null
    this.resetTransientState()
  }

  private resetTransientState(): void {
    this.pendingPointer = null
    this.escapeArmed = false
  }

  private releaseClipBoxDrag(): boolean {
    const boxEditorState = this.clipService?.getBoxEditorState()
    if (!boxEditorState?.active || !boxEditorState.dragging) {
      return false
    }

    this.pendingPointer = null
    this.updateHoverState(null, null, null)
    return true
  }

  private shouldSuspendHoverPicking(event: MeasurePointerEvent): boolean {
    const buttons =
      typeof (event.domEvent as Partial<PointerEvent>).buttons === 'number'
        ? ((event.domEvent as Partial<PointerEvent>).buttons ?? 0)
        : 0
    if (buttons !== 0) {
      return true
    }

    return performance.now() - this.lastCameraMotionAt < MeasurePlugin.CAMERA_MOTION_COOLDOWN_MS
  }

  private trackCameraMotion(camera: THREE.Camera): void {
    const matrixWorldChanged = !this.lastCameraMatrixWorld.equals(camera.matrixWorld)
    const projectionChanged = !this.lastCameraProjectionMatrix.equals(camera.projectionMatrix)
    if (!this.hasCameraSnapshot) {
      this.lastCameraMatrixWorld.copy(camera.matrixWorld)
      this.lastCameraProjectionMatrix.copy(camera.projectionMatrix)
      this.hasCameraSnapshot = true
      return
    }

    if (!matrixWorldChanged && !projectionChanged) {
      return
    }

    this.lastCameraMotionAt = performance.now()
    this.lastCameraMatrixWorld.copy(camera.matrixWorld)
    this.lastCameraProjectionMatrix.copy(camera.projectionMatrix)
  }

  /**
   * 更新鼠标悬停预览状态
   * 测量过程中根据拾取结果刷新预览点和所属视口，避免首点前预览泄漏到其他视口
   */
  private updateHoverState(
    nextPreviewPoint: MeasurePoint | null,
    nextHoverSnap: {
      kind: MeasureSnapResult['kind']
      snapType: MeasureSnapResult['snapType']
      entityId?: string
      assetId?: string
    } | null,
    nextPreviewViewId: ViewId | null
  ): void {
    const effectivePreviewViewId = nextPreviewPoint ? nextPreviewViewId : null
    if (
      this.hasPreviewPointChanged(nextPreviewPoint) ||
      this.hasHoverSnapChanged(nextHoverSnap) ||
      this.hasPreviewViewIdChanged(effectivePreviewViewId)
    ) {
      this.previewPoint = nextPreviewPoint
      this.previewViewId = effectivePreviewViewId
      this.hoverSnap = nextHoverSnap
      this.emitResultChanged()
    }
  }

  /**
   * 判断预览点是否变化
   * 用于避免鼠标悬停预览在位置未变化时重复触发渲染刷新
   */
  private hasPreviewPointChanged(nextPreviewPoint: MeasurePoint | null): boolean {
    if (this.previewPoint === null && nextPreviewPoint === null) {
      return false
    }

    if (this.previewPoint === null || nextPreviewPoint === null) {
      return true
    }

    return !this.previewPoint.position.equals(nextPreviewPoint.position)
  }

  /**
   * 判断预览视口是否变化
   * 坐标测量首点前跟随鼠标跨视口移动时用于触发 overlay 重新过滤
   */
  private hasPreviewViewIdChanged(nextPreviewViewId: ViewId | null): boolean {
    return this.previewViewId !== nextPreviewViewId
  }

  private hasHoverSnapChanged(
    nextHoverSnap: {
      kind: MeasureSnapResult['kind']
      snapType: MeasureSnapResult['snapType']
      entityId?: string
      assetId?: string
    } | null
  ): boolean {
    if (this.hoverSnap === null && nextHoverSnap === null) {
      return false
    }

    if (this.hoverSnap === null || nextHoverSnap === null) {
      return true
    }

    return (
      this.hoverSnap.kind !== nextHoverSnap.kind ||
      this.hoverSnap.snapType !== nextHoverSnap.snapType ||
      this.hoverSnap.entityId !== nextHoverSnap.entityId ||
      this.hoverSnap.assetId !== nextHoverSnap.assetId
    )
  }

  private tryAutoFinish(): boolean {
    const count = this.currentPoints.length
    const type = this._measureType

    if (type === 'coordinate' && this._coordinateMode === 'single' && count === 1) {
      this.finishCurrentMeasure()
      return true
    }

    if ((type === 'height' || type === 'azimuth') && count === 2) {
      this.finishCurrentMeasure()
      return true
    }

    if ((type === 'circle' || type === 'angle') && count === 3) {
      this.finishCurrentMeasure()
      return true
    }

    return false
  }

  private canCompleteCurrentMeasure(): boolean {
    const count = this.currentPoints.length

    switch (this._measureType) {
      case 'coordinate':
        return this._coordinateMode === 'continuous' ? count >= 1 : count === 1
      case 'distance':
        return count >= 2
      case 'height':
      case 'azimuth':
        return count >= 2
      case 'circle':
      case 'angle':
        return count === 3
      case 'area':
        return count >= 3
      default:
        return false
    }
  }

  private emitResultChanged(): void {
    this.context.events.emit('tool.resultChanged', {
      name: this.name,
      data: this.results,
    })
  }

  private invalidateVisual(reason: string): void {
    this.context.renderInvalidation.invalidate(reason)
  }

  private emitDraftChanged(): void {
    if (this.draftChangeHandlers.size === 0) {
      return
    }

    const state = this.getDraftState()
    for (const handler of this.draftChangeHandlers) {
      handler(state)
    }
  }

  private emitPointRemoved(event: MeasurePointRemovalEvent): void {
    for (const handler of this.pointRemovalHandlers) {
      handler(event)
    }
  }

  private validateToken(token: ActivationToken): void {
    if (token !== this.activationToken) {
      throw new Error('MeasurePlugin: invalid ActivationToken')
    }
  }

  private resolveActiveViewCamera(): THREE.Camera | null {
    return getActiveViewCamera(this.viewRegistry)
  }

  private resolveActiveViewRect() {
    return this.viewRegistry?.getActive()?.rect ?? null
  }

  private isControlKey(key: string): boolean {
    return key === 'Control' || key === 'Ctrl'
  }

  private ensureMagnifier(): Magnifier {
    if (!this.magnifier) {
      this.magnifier = new Magnifier(
        this.context,
        () => this.currentCamera ?? this.resolveActiveViewCamera() ?? undefined,
        {
          excludeTextSprite: true,
          occlusionExcludeObjectPredicate: (obj: THREE.Object3D) =>
            this.shouldHideCoveredObject(obj),
        }
      )
    }

    return this.magnifier
  }

  private showMagnifier(): void {
    this.ensureMagnifier().show()
  }

  private hideMagnifier(): void {
    this.cancelPendingMagnifierShow()
    this.magnifier?.hide()
  }

  private scheduleMagnifierShow(): void {
    if (this.magnifierShowTimer !== null) {
      return
    }

    this.magnifierShowTimer = globalThis.setTimeout(() => {
      this.magnifierShowTimer = null
      if (!this._active) {
        return
      }
      this.showMagnifier()
    }, MeasurePlugin.MAGNIFIER_SHOW_DELAY_MS)
  }

  private cancelPendingMagnifierShow(): void {
    if (this.magnifierShowTimer === null) {
      return
    }

    globalThis.clearTimeout(this.magnifierShowTimer)
    this.magnifierShowTimer = null
  }

  private disposeMagnifier(): void {
    this.cancelPendingMagnifierShow()
    if (!this.magnifier) {
      return
    }

    this.magnifier.dispose()
    this.magnifier = null
  }

  private shouldHideCoveredObject(obj: THREE.Object3D): boolean {
    return this.isTextSpriteObject(obj) || this.isCadObject(obj)
  }

  private isTextSpriteObject(obj: THREE.Object3D): boolean {
    if (obj.type !== 'Sprite') {
      return false
    }

    const userData = (obj.userData ?? {}) as Record<string, unknown>
    const name = (obj.name ?? '').toLowerCase()

    return (
      name.includes('text') ||
      '_labelAnchor' in userData ||
      '_textCanvas' in userData ||
      '_textTexture' in userData
    )
  }

  private isCadObject(obj: THREE.Object3D): boolean {
    const cadRoot = this.context.sceneGraph.cadRoot
    let current: THREE.Object3D | null = obj

    while (current) {
      if (current === cadRoot) {
        return true
      }
      current = current.parent
    }

    return false
  }
}

export default MeasurePlugin
